import React, { ReactNode, memo, useMemo } from 'react';
import AntdDropdown, { DropDownProps } from 'antd/es/dropdown';
import cx from 'clsx';

import './index.css';

type DropdownItem = {
  label: ReactNode;
  value?: string | number | boolean;
  render?: (label: ReactNode) => ReactNode;
  onClick?: React.MouseEventHandler<HTMLLIElement>;
  children?: DropdownItem[];
  childrenDropDownProps?: DropdownProps;
};

type DropdownGroupProps = {
  options?: DropdownItem[];
  currentSelectedIdx?: number;
};

const DropdownGroup = memo(({ options = [], currentSelectedIdx }: DropdownGroupProps) => {
  // 深度遍历节点，递增序号
  return (
    <>
      {options.map(({ label, value, render, onClick, children }, idx) => {
        if (children) {
          return children.length ? (
            <div className="db-dropdown-optionGroup">
              <div className="db-dropdown-optionGroupTitle">{label}</div>
              <ul className="db-dropdown-list">
                <DropdownGroup options={children} />
              </ul>
            </div>
          ) : null;
        } else {
          return (
            <li
              key={+idx}
              className={cx(
                'db-dropdown-item',
                typeof currentSelectedIdx !== 'undefined' &&
                  currentSelectedIdx === idx &&
                  'is-selected',
              )}
              onClick={render ? undefined : onClick}
              data-idx={idx}
              data-label={label}
              data-value={value}>
              {render ? render(label) : label}
            </li>
          );
        }
      })}
    </>
  );
});

const CascadingMenu = memo(({ options = [], currentSelectedIdx }: DropdownGroupProps) => {
  return (
    <>
      {options.map(
        ({ label, value, render, onClick, children, childrenDropDownProps = {} }, idx) => {
          const content = render ? render(label) : label;
          return (
            <li
              key={+idx}
              className={cx(
                'db-dropdown-item',
                typeof currentSelectedIdx !== 'undefined' &&
                  currentSelectedIdx === idx &&
                  'is-selected',
              )}
              onClick={render ? undefined : onClick}
              data-idx={idx}
              data-label={label}
              data-value={value}>
              {children?.length ? (
                <Dropdown
                  data={children}
                  trigger={['hover']}
                  inParent
                  placement="topRight"
                  {...childrenDropDownProps}>
                  <div>{content}</div>
                </Dropdown>
              ) : (
                content
              )}
            </li>
          );
        },
      )}
    </>
  );
});

export type DropdownProps = {
  prefix?: ReactNode;
  data?: DropdownItem[];
  subfix?: ReactNode;
  children?: ReactNode;
  inParent?: boolean;
  menuType?: 'default' | 'cascade';
  currentSelectedIdx?: number;
  dropdownListClassName?: string;
} & Omit<DropDownProps, 'overlay'>;

export const Dropdown = ({
  data,
  prefix,
  subfix,
  children,
  className,
  inParent,
  menuType = 'default',
  currentSelectedIdx,
  placement = 'bottomRight',
  trigger = ['click'],
  dropdownListClassName,
  ...rest
}: DropdownProps) => {
  const MenuList = useMemo(
    () => (menuType === 'cascade' ? CascadingMenu : DropdownGroup),
    [menuType],
  );
  return (
    <AntdDropdown
      {...rest}
      placement={placement}
      trigger={trigger}
      className={cx('db-dropdown', className)}
      getPopupContainer={inParent ? dom => dom.parentElement! : undefined}
      overlay={
        <div className="db-dropdown-pannel">
          {prefix}
          <ul className={cx('db-dropdown-list', dropdownListClassName)}>
            <MenuList options={data} currentSelectedIdx={currentSelectedIdx} />
          </ul>
          {subfix}
        </div>
      }>
      {children}
    </AntdDropdown>
  );
};

export default Dropdown;
